import { format, addDays, subDays, isSameDay, isToday } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DateRibbonProps {
  startDate: Date;
  daysToShow?: number;
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onShiftDates: (days: number) => void;
}

export function DateRibbon({
  startDate,
  daysToShow = 30,
  selectedDate,
  onDateSelect,
  onShiftDates,
}: DateRibbonProps): React.ReactElement {
  // Generate array of dates
  const dates = Array.from({ length: daysToShow }, (_, i) => addDays(startDate, i));
  const firstVisibleDate = dates[0]!;
  const lastVisibleDate = dates[dates.length - 1]!;

  // Move selection to previous day, shift ribbon only if at first visible date
  const handlePrevious = (): void => {
    const prevDate = subDays(selectedDate, 1);
    if (isSameDay(selectedDate, firstVisibleDate)) {
      // At first visible date - shift ribbon back
      onShiftDates(-1);
    }
    onDateSelect(prevDate);
  };

  // Move selection to next day, shift ribbon only if at last visible date
  const handleNext = (): void => {
    const nextDate = addDays(selectedDate, 1);
    if (isSameDay(selectedDate, lastVisibleDate)) {
      // At last visible date - shift ribbon forward
      onShiftDates(1);
    }
    onDateSelect(nextDate);
  };

  return (
    <div className="flex items-stretch border-b border-border/50 bg-background/95 backdrop-blur-xs">
      <button
        type="button"
        onClick={handlePrevious}
        className="shrink-0 w-7 flex items-center justify-center border-r border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors focus:outline-hidden"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <div className="flex-1 flex items-stretch">
        {dates.map((date) => {
          const isSelected = isSameDay(date, selectedDate);
          const isTodayDate = isToday(date);
          const dateKey = format(date, 'yyyy-MM-dd');

          const handleClick = (): void => {
            onDateSelect(date);
          };

          return (
            <div
              key={dateKey}
              role="button"
              tabIndex={0}
              onClick={handleClick}
              onKeyDown={(e) => e.key === 'Enter' && handleClick()}
              className={cn(
                'flex-1 min-w-[32px] flex flex-col items-center justify-center py-1 transition-all cursor-pointer select-none',
                'hover:bg-muted/80',
                isSelected && 'bg-highlight text-highlight-foreground hover:bg-highlight/80'
              )}
            >
              <span
                className={cn(
                  'text-[8px] font-medium uppercase tracking-wide pointer-events-none',
                  isSelected ? 'text-highlight-foreground/80' : 'text-muted-foreground',
                  isTodayDate && !isSelected && 'text-blue-600 dark:text-blue-400 font-semibold'
                )}
              >
                {format(date, 'EEE').charAt(0)}
              </span>
              <span
                className={cn(
                  'text-sm font-semibold leading-tight pointer-events-none',
                  isSelected ? 'text-highlight-foreground' : 'text-foreground',
                  isTodayDate && !isSelected && 'text-blue-600 dark:text-blue-400'
                )}
              >
                {format(date, 'd')}
              </span>
              <span
                className={cn(
                  'text-[9px] uppercase pointer-events-none',
                  isSelected ? 'text-highlight-foreground/60' : 'text-muted-foreground/70',
                  isTodayDate && !isSelected && 'text-blue-600/70 dark:text-blue-400/70'
                )}
              >
                {format(date, 'MMM')}
              </span>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleNext}
        className="shrink-0 w-7 flex items-center justify-center border-l border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors focus:outline-hidden"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
